import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, X, AlertCircle, Info } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const Toast = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleRemove = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => onRemove(toast.id), 300);
  }, [toast.id, onRemove]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      handleRemove();
    }, toast.duration || 3000);

    return () => clearTimeout(timer);
  }, [handleRemove, toast.duration]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-indigo-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'info':
        return <Info className="h-5 w-5 text-indigo-500" />;
      default:
        return <CheckCircle className="h-5 w-5 text-indigo-500" />;
    }
  };

  const getBackgroundColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-indigo-50 border-indigo-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'info':
        return 'bg-indigo-50 border-indigo-200';
      default:
        return 'bg-indigo-50 border-indigo-200';
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 max-w-sm w-full bg-background border rounded-lg shadow-lg p-4 transition-all duration-300 ease-in-out',
        getBackgroundColor(),
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      )}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{toast.title}</p>
          {toast.description && (
            <p className="text-sm text-muted-foreground mt-1">{toast.description}</p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleRemove}
          className="h-6 w-6 p-0 hover:bg-muted/50"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = {
      id,
      type: 'success',
      duration: 3000,
      ...toast,
    };
    
    setToasts(prev => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const value = {
    addToast,
    removeToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 space-y-2 flex flex-col items-center">
        {toasts.map(toast => (
          <Toast key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
